/**
 * Badges (achievements). Six families, each with bronze / silver / gold / diamond tiers,
 * counted across every club the player belongs to, including matches played before
 * badges existed. Numbers agreed with David on 2026-10-14.
 *
 * Everything here is pure: callers pass each club's archived sessions and events plus
 * the player's name in that club, and get back progress and unlock dates.
 */
import {
  isRankedSession,
  isClassicSession,
  isCompletedClassicMatch,
  getClassicTeams,
  getRotationMatchOutcome,
  getSessionGameStats,
} from './matchStats';
import { normalizeEvent } from './eventsEngine';

export const BADGE_TIERS = ['bronze', 'silver', 'gold', 'diamond'];
export const TIER_LABELS = { bronze: 'Bronze', silver: 'Silver', gold: 'Gold', diamond: 'Diamond' };

// Order = grid columns (Figma 791:19302): one family per column, tiers climbing down the rows
export const BADGE_FAMILIES = [
  { key: 'doubles', name: 'Doubles wins', thresholds: [5, 25, 50, 100], goal: (n) => `Win ${n} doubles matches`, unit: ['doubles match', 'doubles matches'], verb: 'Win' },
  { key: 'games', name: 'Game wins', thresholds: [50, 250, 500, 1000], goal: (n) => `Win ${n} games`, unit: ['game', 'games'], verb: 'Win' },
  { key: 'streak', name: 'Game streak', thresholds: [5, 8, 12, 15], goal: (n) => `Win ${n} games in a row`, unit: ['game in a row', 'games in a row'], verb: 'Win' },
  { key: 'mvp', name: 'MVP wins', thresholds: [1, 5, 10, 25], goal: (n) => (n === 1 ? 'Be MVP of a rotation' : `Be MVP of ${n} rotations`), unit: ['MVP', 'MVPs'], verb: 'Win' },
  { key: 'serves', name: 'Wins from serve', thresholds: [25, 100, 250, 500], goal: (n) => `Win ${n} games on your serve`, unit: ['game on serve', 'games on serve'], verb: 'Win' },
  { key: 'event', name: 'Event wins', thresholds: [1, 3, 5, 10], goal: (n) => (n === 1 ? 'Win an event' : `Win ${n} events`), unit: ['event', 'events'], verb: 'Win' },
];

export const BADGES = BADGE_TIERS.flatMap((tier, tierIndex) =>
  BADGE_FAMILIES.map((family) => ({
    id: `${family.key}-${tier}`,
    family: family.key,
    familyName: family.name,
    tier,
    tierIndex,
    threshold: family.thresholds[tierIndex],
    goal: family.goal(family.thresholds[tierIndex]),
  }))
);

const nameOf = (p) => {
  if (!p) return '';
  if (typeof p === 'object') return String(p.name || p.id || p.uid || '').trim();
  return String(p).trim();
};

const same = (a, b) => Boolean(a) && Boolean(b) && nameOf(a).toLowerCase() === nameOf(b).toLowerCase();
const includesName = (team, name) => Array.isArray(team) && team.some((p) => same(p, name));

/** Milliseconds from a number, ISO string, day key or Firestore Timestamp; null if unknown. */
export function toMillis(val) {
  if (val == null) return null;
  if (typeof val === 'number') return Number.isFinite(val) ? val : null;
  if (typeof val.toMillis === 'function') return val.toMillis();
  if (typeof val.seconds === 'number') return val.seconds * 1000;
  if (typeof val === 'string') {
    const ms = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(val) ? `${val}T12:00:00` : val);
    return Number.isNaN(ms) ? null : ms;
  }
  return null;
}

const sessionMillis = (s) =>
  toMillis(s.timestamp) ?? toMillis(s.date) ?? toMillis(s.sessionDate) ?? toMillis(s.createdAt) ?? toMillis(s.archivedAt);

/** Per-game records for a session, in play order. Empty when the session only kept totals. */
function sessionGames(session) {
  if (isClassicSession(session)) {
    if (Array.isArray(session.classicGameHistory) && session.classicGameHistory.length > 0) return session.classicGameHistory;
    return (session.sessionHistory || []).filter((g) => g && g.winningTeam && g.losingTeam && g.scores);
  }
  return (session.sessionHistory || []).filter((g) => g && Array.isArray(g.winningTeam));
}

/** Doubles means two a side: a classic match with pairs, or a rotation of 4–6 players. */
function isDoublesSession(session) {
  if (isClassicSession(session)) {
    const [winners, losers] = getClassicTeams(session);
    return winners.length === 2 && losers.length === 2;
  }
  const games = sessionGames(session);
  if (games.length > 0) return games.some((g) => (g.winningTeam || []).length === 2);
  return (session.sessionPlayers || []).length >= 4;
}

/**
 * One timeline of countable moments for this player across all their clubs.
 * clubs: [{ playerName, archivedSessions, events }]
 */
function buildTimeline(clubs) {
  const moments = []; // { at, kind, won?, served? }

  clubs.forEach(({ playerName, archivedSessions = [], events = [] }) => {
    if (!playerName) return;

    archivedSessions.forEach((session) => {
      if (!isRankedSession(session)) return;
      const at = sessionMillis(session) ?? 0;
      const games = sessionGames(session);
      const played = games.filter((g) => includesName(g.winningTeam, playerName) || includesName(g.losingTeam, playerName));

      if (played.length > 0) {
        played.forEach((g, i) => {
          moments.push({
            at: toMillis(g.timestamp) ?? at + i,
            order: i,
            kind: 'game',
            won: includesName(g.winningTeam, playerName),
            served: same(g.serving, playerName),
          });
        });
      } else {
        // Older sessions kept only totals: they count for game wins but not streaks or serves
        const totals = Object.entries(getSessionGameStats(session)).find(([n]) => same(n, playerName))?.[1];
        if (totals?.gw) moments.push({ at, kind: 'games-total', count: totals.gw });
      }

      // Match result, once the session's games are in
      const matchAt = at + played.length + 1;
      if (isClassicSession(session)) {
        if (isCompletedClassicMatch(session) && isDoublesSession(session)) {
          const [winners] = getClassicTeams(session);
          if (includesName(winners, playerName)) moments.push({ at: matchAt, kind: 'doubles-win' });
        }
      } else {
        const outcome = getRotationMatchOutcome(session);
        if (isDoublesSession(session) && includesName(outcome.winners, playerName)) moments.push({ at: matchAt, kind: 'doubles-win' });
        if (same(outcome.mvpPlayer, playerName)) moments.push({ at: matchAt, kind: 'mvp' });
      }
    });

    events.forEach((ev) => {
      const event = normalizeEvent(ev);
      if (event.status !== 'ended' || !event.winner) return;
      const winners = Array.isArray(event.winner.players) && event.winner.players.length > 0
        ? event.winner.players
        : [event.winner.player];
      if (includesName(winners.filter((p) => p && p !== 'Tie'), playerName)) {
        moments.push({ at: toMillis(event.endDate) ?? toMillis(event.winner.decidedAt) ?? 0, kind: 'event-win' });
      }
    });
  });

  return moments.sort((a, b) => a.at - b.at || (a.order ?? 0) - (b.order ?? 0));
}

/**
 * Progress for every badge family and the date each tier unlocked.
 * Returns { counts: { family: value }, unlocked: { badgeId: ms|null } }
 * (null = unlocked but the date isn't known).
 */
export function computeBadges(clubs = []) {
  const counts = { doubles: 0, games: 0, streak: 0, mvp: 0, serves: 0, event: 0 };
  const unlocked = {};
  let run = 0;

  const bump = (family, value, at) => {
    if (value <= counts[family]) return;
    counts[family] = value;
    const fam = BADGE_FAMILIES.find((f) => f.key === family);
    fam.thresholds.forEach((t, i) => {
      const id = `${family}-${BADGE_TIERS[i]}`;
      if (value >= t && !(id in unlocked)) unlocked[id] = at || null;
    });
  };

  buildTimeline(clubs).forEach((m) => {
    switch (m.kind) {
      case 'game':
        if (m.won) {
          bump('games', counts.games + 1, m.at);
          run += 1;
          bump('streak', run, m.at);
          if (m.served) bump('serves', counts.serves + 1, m.at);
        } else {
          run = 0;
        }
        break;
      case 'games-total':
        bump('games', counts.games + m.count, m.at);
        break;
      case 'doubles-win':
        bump('doubles', counts.doubles + 1, m.at);
        break;
      case 'mvp':
        bump('mvp', counts.mvp + 1, m.at);
        break;
      case 'event-win':
        bump('event', counts.event + 1, m.at);
        break;
      default:
    }
  });

  return { counts, unlocked };
}

/** What's left to unlock a badge, for the locked detail panel. */
export function badgeProgress(badge, counts) {
  const family = BADGE_FAMILIES.find((f) => f.key === badge.family);
  const current = Math.min(counts?.[badge.family] ?? 0, badge.threshold);
  const remaining = badge.threshold - current;
  let hint;
  if (badge.family === 'streak') {
    hint = `Your best run is ${counts?.streak ?? 0}. Win ${badge.threshold} games in a row in any mode to unlock`;
  } else {
    const unit = family.unit[remaining === 1 ? 0 : 1];
    hint = `${family.verb} ${remaining} more ${unit} to unlock`;
  }
  return { current, remaining, ratio: badge.threshold ? current / badge.threshold : 0, hint };
}

// Achievements page (Figma 852:3353): David's card order and headers, one card per family
export const ACHIEVEMENT_CARDS = [
  { family: 'doubles', title: 'Doubles match wins', count: (n) => `${n} match ${n === 1 ? 'win' : 'wins'}` },
  { family: 'games', title: 'Total game wins', count: (n) => `${n.toLocaleString('en-GB')} game ${n === 1 ? 'win' : 'wins'}` },
  { family: 'event', title: 'Event wins', count: (n) => `${n} event ${n === 1 ? 'win' : 'wins'}` },
  { family: 'mvp', title: 'MVP wins', count: (n) => `${n} MVP ${n === 1 ? 'win' : 'wins'}` },
  { family: 'streak', title: 'Game streak', count: (n) => `${n} ${n === 1 ? 'game' : 'games'}` },
  { family: 'serves', title: 'Wins on serve', count: (n) => `${n.toLocaleString('en-GB')} ${n === 1 ? 'game' : 'games'} on serve` },
];

/**
 * Where a player stands in one family: the highest tier reached (null below bronze),
 * the next tier to chase (null at diamond) and progress towards it.
 */
export function familyStatus(familyKey, counts = {}) {
  const family = BADGE_FAMILIES.find((f) => f.key === familyKey);
  const value = counts[familyKey] ?? 0;
  const reached = family.thresholds.filter((t) => value >= t).length;
  const badge = reached > 0 ? BADGES.find((b) => b.id === `${familyKey}-${BADGE_TIERS[reached - 1]}`) : null;
  const next = reached < BADGE_TIERS.length ? BADGES.find((b) => b.id === `${familyKey}-${BADGE_TIERS[reached]}`) : null;
  const remaining = next ? next.threshold - value : 0;
  // A streak isn't added to, it has to be done in one go
  const hint = !next ? null
    : familyKey === 'streak' ? `Win ${next.threshold} in a row to unlock ${next.tier}`
    : `Win ${remaining} more to unlock ${next.tier}`;
  return { value, badge, next, ratio: next ? Math.min(value / next.threshold, 1) : 1, hint };
}

/** The current tier badge of each family the player has reached bronze in, in card order. */
export function currentTierBadges(counts = {}) {
  return ACHIEVEMENT_CARDS.map((c) => familyStatus(c.family, counts).badge).filter(Boolean);
}

// Achievement dialog copy (Figma 849:3295): "You've unlocked a gold badge for winning <10 events>."
const CHEERS = {
  doubles: (n) => ['winning', `${n} doubles matches`],
  games: (n) => ['winning', `${n.toLocaleString('en-GB')} games`],
  streak: (n) => ['winning', `${n} games in a row`],
  mvp: (n) => (n === 1 ? ['being', 'MVP of a rotation'] : ['being MVP', `${n} times`]),
  serves: (n) => ['winning', `${n} games on your serve`],
  event: (n) => (n === 1 ? ['winning', 'an event'] : ['winning', `${n} events`]),
};

/** { tier, verb, highlight } for "You've unlocked a <tier> badge for <verb> <highlight>". */
export function celebrationCopy(badge) {
  const [verb, highlight] = CHEERS[badge.family](badge.threshold);
  return { tier: badge.tier, verb, highlight };
}

/** Most recently unlocked first; badges with unknown dates go last. */
export function recentBadges(unlocked, limit = 4) {
  return BADGES.filter((b) => b.id in unlocked)
    .sort((a, b) => (unlocked[b.id] ?? -1) - (unlocked[a.id] ?? -1) || b.tierIndex - a.tierIndex)
    .slice(0, limit);
}

export function formatUnlockDate(ms) {
  if (!ms) return null;
  const d = new Date(ms);
  const day = d.getDate();
  const suffix = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
  const weekday = d.toLocaleDateString('en-GB', { weekday: 'short' });
  const month = d.toLocaleDateString('en-GB', { month: 'long' });
  const year = d.getFullYear() !== new Date().getFullYear() ? ` ${d.getFullYear()}` : '';
  return `${weekday} ${day}${suffix} ${month}${year}`;
}
